import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  chartWrapper: {
    display: 'grid',
    gridAutoRows: theme.marginBase,
    width: '100%',
    backgroundColor: theme.colors.grayBackground,
  },
  card: {
    width: '100%',
  },
  cardContent: {
    borderRadius: theme.borderRadius.std,
    backgroundColor: 'white',
    margin: 'auto',
  },
}));

interface GridOptions {
  gap: number;
  minCardWidth: number;
  cardWidth: 'auto' | 'min-width';
}

type Props = React.HTMLProps<HTMLDivElement> & Partial<GridOptions>;
type CardProps = React.HTMLProps<HTMLDivElement> & { colSpan?: number };

const useDefaults = (): GridOptions => {
  const theme = useTheme<Theme>();
  return {
    gap: theme.marginBase * 3,
    minCardWidth: theme.maxContentWidth.defaultGrid,
    cardWidth: 'min-width',
  };
};

const GridContext = React.createContext<GridOptions | null>(null);
export const useMansoryGrid = (): GridOptions => {
  const defaults = useDefaults();
  const context = React.useContext(GridContext) || {};
  return { ...defaults, ...context };
};

export const MansoryGrid = ({
  className,
  gap,
  minCardWidth,
  cardWidth,
  style = {},
  ...props
}: Props) => {
  const classes = useStyles();

  const defaults = useDefaults();
  const context: GridOptions = useMemo(
    () => ({
      gap: gap || defaults.gap,
      minCardWidth: minCardWidth || defaults.minCardWidth,
      cardWidth: cardWidth === 'auto' ? 'auto' : 'min-width',
    }),
    [gap, minCardWidth, cardWidth, defaults],
  );
  const divStyle = useMemo(
    () => ({
      ...style,
      gridTemplateColumns: `repeat(auto-fit, minmax(${context.minCardWidth}px, 1fr))`,
      gridColumnGap: context.gap,
    }),
    [context, style],
  );

  return (
    <GridContext.Provider value={context}>
      <div
        {...props}
        style={divStyle}
        className={classNames(classes.chartWrapper, className)}
      />
    </GridContext.Provider>
  );
};

export const MansoryCard = ({
  className,
  style,
  children,
  colSpan,
  ...props
}: Props) => {
  const classes = useStyles();
  const [height, setHeight] = useState(0);
  const context = useMansoryGrid();
  const theme = useTheme<Theme>();
  const gap = context?.gap || 0;

  const containerStyle = useMemo(() => {
    const span = Math.trunc((height + gap) / 8);
    return {
      ...style,
      gridRowEnd: `span ${span}`,
      gridColumnEnd: colSpan ? `span ${colSpan}` : 'auto',
    };
  }, [context, style, height, colSpan, gap]);

  const cardStyle = useMemo(() => {
    const span = colSpan || 1;
    return {
      width:
        context?.cardWidth === 'auto'
          ? 'auto'
          : (context?.minCardWidth || 0) * span + gap * (span - 1),
    };
  }, [context, theme, colSpan, gap]);

  const onRef = useCallback(
    (ref: HTMLDivElement | null) => {
      if (!ref) return;
      setHeight(ref.clientHeight);
    },
    [setHeight],
  );

  return (
    <div
      {...props}
      className={classNames(classes.card, className)}
      style={containerStyle}
    >
      <div className={classes.cardContent} style={cardStyle} ref={onRef}>
        {children}
      </div>
    </div>
  );
};
